/**
 * CommandrManager.ts - Commandr - Universal keyboard shortcut manager
 * 
 * CHANGE HISTORY:
 * - 251021_2025 [cc6aae8] danielbeer: fix: Resolve TypeScript migration errors
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
export class Commandr {
  constructor(options: any = {}) {
    console.log('[Commandr] Constructor called');
    
    this.actr = options.actr;
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
    this.shortcuts = new Map(); // key -> handler
    this.scopes = new Map(); // scope -> shortcuts
    this.activeScope = 'global';
    this.enabled = true;
    this.listeners = new Set();
    this.isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    
    // Bind event listener
    this.handleKeyDown = this.handleKeyDown.bind(this);
    
    // Start listening
    if (typeof window !== 'undefined') {
      console.log('[Commandr] Attaching keydown listener to window');
      window.addEventListener('keydown', this.handleKeyDown);
    }
  }
  
  /**
   * Register Commandr actions with Actr
   * 
   * ARCHITECTURE NOTE: Commandr uses ACTIONS-ONLY pattern
   * 
   * Why this is different from notifyr/groupr:
   * 1. In-Memory Registry: Shortcuts stored in memory only
   *    - Shortcuts registered at runtime (Map data structures)
   *    - No persistence needed - re-registered on app start
   *    - CRUD would add unnecessary overhead
   * 
   * 2. Event-Driven: Keyboard shortcuts are event handlers
   *    - Shortcuts are functions, not data entities
   *    - Triggered by keyboard events (not CRUD operations)
   *    - Ephemeral - exist only while app is running
   * 
   * 3. High-Frequency Lookups: Every keypress checked
   *    - Every keydown event triggers lookup
   *    - Performance-critical (must be <1ms)
   *    - CRUD overhead would degrade responsiveness
   * 
   * Pattern: Actions-Only (No CRUD)
   * - Actions: For shortcut operations (register, unregister, trigger)
   * - No CRUD: Shortcuts are event handlers, not persisted entities
   * - Memory-based: Registry stored in Map, not database
   * 
   * Compare to other patterns:
   * - permittr: Actions-Only (performance-critical, in-memory cache)
   * - backtrackr: Actions-Only (in-memory state, complex transitions)
   * - commandr: Actions-Only (event-driven, high-frequency lookups)
   * 
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/commandr/README.md "Actr Integration" section
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // register action
    this.actr.register('commandr:register', {
      handler: async (params) => {
        return this.register(params.key, params.handler, params.options);
      },
      description: 'Register keyboard shortcut'
    });
    
    // unregister action
    this.actr.register('commandr:unregister', {
      handler: async (params) => {
        return this.unregister(params.key);
      },
      description: 'Unregister keyboard shortcut'
    });
    
    // setScope action
    this.actr.register('commandr:set-scope', {
      handler: async (params) => {
        return this.setScope(params.scope);
      },
      description: 'Set keyboard shortcut scope'
    });
    
    // trigger action (programmatic shortcut execution)
    this.actr.register('commandr:trigger', {
      handler: async (params) => {
        return this.trigger(params.key);
      },
      description: 'Trigger keyboard shortcut programmatically'
    });
    
    // Note: No CRUD operations - shortcuts are event handlers stored in memory.
    // They are re-registered on app start, not persisted to database.
  }
  
  /**
   * Register a keyboard shortcut
   * @param {string} key - Key combination (e.g., 'cmd+n', 'ctrl+shift+s')
   * @param {Function} handler - Handler function
   * @param {Object} options - Options (scope, description, preventDefault)
   */
  register(key, handler, options = {}) {
    const {
      scope = 'global',
      description = '',
      preventDefault = true,
      enabled = true
    } = options;
    
    const normalizedKey = this.normalizeKey(key);
    const shortcut = {
      key: normalizedKey,
      handler,
      scope,
      description,
      preventDefault,
      enabled
    };
    
    // Store in shortcuts map
    this.shortcuts.set(normalizedKey, shortcut);
    
    // Store in scope map
    if (!this.scopes.has(scope)) {
      this.scopes.set(scope, new Map());
    }
    this.scopes.get(scope).set(normalizedKey, shortcut);
    
    return () => this.unregister(normalizedKey);
  }
  
  /**
   * Unregister a shortcut
   */
  unregister(key) {
    const normalizedKey = this.normalizeKey(key);
    const shortcut = this.shortcuts.get(normalizedKey);
    
    if (shortcut) {
      this.shortcuts.delete(normalizedKey);
      
      const scopeShortcuts = this.scopes.get(shortcut.scope);
      if (scopeShortcuts) {
        scopeShortcuts.delete(normalizedKey);
      }
    }
  }
  
  /**
   * Set active scope
   */
  setScope(scope) {
    this.activeScope = scope;
    this.notifyListeners('scopeChanged', scope);
  }
  
  /**
   * Get active scope
   */
  getScope() {
    return this.activeScope;
  }
  
  /**
   * Enable/disable shortcuts
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }
  
  /**
   * Handle keydown event
   */
  handleKeyDown(event) {
    // Debug ALL z key presses
    if (event.key === 'z' || event.key === 'Z') {
      console.log('[Commandr] handleKeyDown called for z:', {
        enabled: this.enabled,
        key: event.key,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey
      });
    }
    
    if (!this.enabled) return;
    
    // Check if target is an input element
    const target = event.target;
    const isInput = target.tagName === 'INPUT' || 
                    target.tagName === 'TEXTAREA' || 
                    target.isContentEditable;
    
    const key = this.getKeyFromEvent(event);
    
    // Debug redo shortcut
    if (key.includes('shift') && key.includes('z')) {
      console.log('[Commandr] handleKeyDown for shift+z:', {
        key,
        activeScope: this.activeScope,
        hasInActiveScope: this.scopes.get(this.activeScope)?.has(key),
        hasInGlobal: this.scopes.get('global')?.has(key),
        allKeysInActiveScope: Array.from(this.scopes.get(this.activeScope)?.keys() || [])
      });
    }
    
    // Try active scope first
    let shortcut = this.scopes.get(this.activeScope)?.get(key);
    
    // Fall back to global scope
    if (!shortcut) {
      shortcut = this.scopes.get('global')?.get(key);
    }
    
    if (shortcut && shortcut.enabled) {
      // Check if shortcut is allowed in inputs
      if (isInput && !shortcut.allowInInput) {
        return;
      }
      
      if (shortcut.preventDefault) {
        event.preventDefault();
      }
      
      shortcut.handler(event);
      this.notifyListeners('shortcutTriggered', { key, shortcut });
    }
  }
  
  /**
   * Get key combination from event
   */
  getKeyFromEvent(event) {
    const parts = [];
    
    if (event.ctrlKey) parts.push('ctrl');
    if (event.altKey) parts.push('alt');
    if (event.shiftKey) parts.push('shift');
    if (event.metaKey) parts.push('cmd');
    
    // Add the actual key
    let key = event.key.toLowerCase();
    
    // Normalize special keys
    if (key === ' ') key = 'space';
    if (key === 'escape') key = 'esc';
    if (key === 'delete') key = 'del';
    if (key === 'backspace') key = 'backspace';
    if (key === 'enter') key = 'enter';
    if (key === 'tab') key = 'tab';
    if (key === 'arrowup') key = 'up';
    if (key === 'arrowdown') key = 'down';
    if (key === 'arrowleft') key = 'left';
    if (key === 'arrowright') key = 'right';
    
    parts.push(key);
    
    const result = parts.join('+');
    
    // Debug redo shortcut
    if (key === 'z' && event.shiftKey && event.metaKey) {
      console.log('[Commandr] getKeyFromEvent for Cmd+Shift+Z:', {
        raw: result,
        normalized: this.normalizeKey(result),
        event: { key: event.key, shiftKey: event.shiftKey, metaKey: event.metaKey }
      });
    }
    
    return result;
  }
  
  /**
   * Normalize key combination
   */
  normalizeKey(key) {
    const parts = key.toLowerCase().split('+').map(p => p.trim());
    const modifiers = [];
    let mainKey = '';
    
    parts.forEach(part => {
      if (['ctrl', 'alt', 'shift', 'cmd', 'meta'].includes(part)) {
        if (part === 'meta') part = 'cmd';
        modifiers.push(part);
      } else {
        mainKey = part;
      }
    });
    
    // Sort modifiers for consistency
    modifiers.sort();
    
    return [...modifiers, mainKey].join('+');
  }
  
  /**
   * Get display string for key combination
   */
  getDisplayString(key) {
    const parts = key.split('+');
    const symbols = {
      'cmd': this.isMac ? '⌘' : 'Ctrl',
      'ctrl': this.isMac ? '⌃' : 'Ctrl',
      'alt': this.isMac ? '⌥' : 'Alt',
      'shift': this.isMac ? '⇧' : 'Shift',
      'enter': '↵',
      'esc': 'Esc',
      'del': this.isMac ? '⌫' : 'Del',
      'backspace': '⌫',
      'space': 'Space',
      'up': '↑',
      'down': '↓',
      'left': '←',
      'right': '→',
      'tab': '⇥'
    };
    
    return parts.map(part => {
      return symbols[part] || part.toUpperCase();
    }).join(this.isMac ? '' : '+');
  }
  
  /**
   * Get all shortcuts for a scope
   */
  getShortcuts(scope = null) {
    if (scope) {
      return Array.from(this.scopes.get(scope)?.values() || []);
    }
    return Array.from(this.shortcuts.values());
  }
  
  /**
   * Get all scopes
   */
  getScopes() {
    return Array.from(this.scopes.keys());
  }
  
  /**
   * Subscribe to events
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  /**
   * Notify listeners
   */
  notifyListeners(event, ...args) {
    this.listeners.forEach(listener => {
      try {
        listener(event, ...args);
      } catch (error) {
        console.error('Commandr listener error:', error);
      }
    });
  }
  
  /**
   * Cleanup
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeyDown);
    }
    this.shortcuts.clear();
    this.scopes.clear();
    this.listeners.clear();
  }
}

// Singleton instance
let instance = null;

export const getCommandr = () => {
  if (!instance) {
    instance = new Commandr();
  }
  return instance;
};

export default Commandr;
